// src/features/stock/pages/StockMovementsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Filter,
  Download,
  Calendar,
  Package,
  ArrowDownCircle,
  ArrowUpCircle,
  RefreshCw,
  Search,
  FileText,
  Printer,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { format, subDays, subMonths } from 'date-fns';
import { useStockMovement } from '../hooks/useStockMovement';
import stockApi from '../services/stockApi';
import { MOVEMENT_TYPES } from '../constants/stockConstants';
import Button from '../../../shared/components/Button';
import Card from '../../../shared/components/Card';
import Input from '../../../shared/components/Input';
import Spinner from '../../../shared/components/Spinner';
import StockMovementForm from '../components/StockMovementForm';
import StockMovementDetails from '../components/StockMovementDetails';

const StockMovementPage = () => {
  const { id } = useParams(); // Optional item ID
  const navigate = useNavigate();
  const { movements, loading, pagination, fetchMovements, getItemMovements } = useStockMovement();
  
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [item, setItem] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    from_date: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
    to_date: format(new Date(), 'yyyy-MM-dd'),
    search: '',
    reference: '',
    performed_by: '',
    location: '',
  });
  const [stats, setStats] = useState({
    totalIn: 0,
    totalOut: 0,
    totalAdjustments: 0,
    netChange: 0,
  });

  useEffect(() => {
    if (id) {
      fetchItemDetails();
      getItemMovements(id, {
        from_date: filters.from_date,
        to_date: filters.to_date,
      });
    } else {
      fetchMovements({
        from_date: filters.from_date,
        to_date: filters.to_date,
        type: filters.type,
        search: filters.search,
      });
    }
  }, [id, filters.from_date, filters.to_date, filters.type, filters.search]);

  useEffect(() => {
    calculateStats();
  }, [movements]);

  const fetchItemDetails = async () => {
    try {
      const response = await stockApi.getStockItem(id);
      setItem(response.data);
    } catch (error) {
      console.error('Failed to fetch item details:', error);
    }
  };

  const calculateStats = () => {
    const stats = movements.reduce((acc, movement) => {
      switch (movement.type) {
        case 'in':
        case 'return':
          acc.totalIn += movement.quantity;
          break;
        case 'out':
        case 'damaged':
        case 'expired':
          acc.totalOut += movement.quantity;
          break;
        case 'adjustment':
          acc.totalAdjustments += movement.quantity;
          break;
      }
      return acc;
    }, { totalIn: 0, totalOut: 0, totalAdjustments: 0 });

    setStats({
      ...stats,
      netChange: stats.totalIn - stats.totalOut,
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      type: '',
      from_date: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
      to_date: format(new Date(), 'yyyy-MM-dd'),
      search: '',
      reference: '',
      performed_by: '',
      location: '',
    });
  };

  const handleViewMovement = (movement) => {
    setSelectedMovement(movement);
  };

  const handleCloseDetails = () => {
    setSelectedMovement(null);
  };

  const handleExport = async () => {
    try {
      const params = {
        ...filters,
        item_id: id,
        format: 'csv',
      };
      const response = await stockApi.exportStockMovements(params);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stock-movements-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export movements:', error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getMovementIcon = (type) => {
    switch (type) {
      case 'in':
      case 'return':
        return <ArrowDownCircle className="h-5 w-5 text-green-600" />;
      case 'out':
      case 'damaged':
      case 'expired':
        return <ArrowUpCircle className="h-5 w-5 text-red-600" />;
      default:
        return <RefreshCw className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getMovementTypeColor = (type) => {
    const colors = {
      in: 'bg-green-100 text-green-800',
      out: 'bg-red-100 text-red-800',
      adjustment: 'bg-yellow-100 text-yellow-800',
      return: 'bg-blue-100 text-blue-800',
      transfer: 'bg-purple-100 text-purple-800',
      damaged: 'bg-orange-100 text-orange-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => id ? navigate(`/stock/${id}`) : navigate('/stock')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {id ? `Stock Movements - ${item?.name}` : 'Stock Movements'}
            </h1>
            <p className="text-gray-600 mt-1">
              {id 
                ? `Track all movements for this item` 
                : 'View and manage all stock transactions'}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            icon={Printer}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            variant="secondary"
            icon={Download}
            onClick={handleExport}
          >
            Export
          </Button>
          {id && (
            <Button
              variant="primary"
              icon={Package}
              onClick={() => setShowForm(true)}
            >
              New Movement
            </Button>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      {!id && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Stock In</p>
                <p className="text-2xl font-bold text-green-600">{stats.totalIn}</p>
              </div>
              <ArrowDownCircle className="h-8 w-8 text-green-500" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Stock Out</p>
                <p className="text-2xl font-bold text-red-600">{stats.totalOut}</p>
              </div>
              <ArrowUpCircle className="h-8 w-8 text-red-500" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Adjustments</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.totalAdjustments}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-yellow-500" />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Net Change</p>
                <p className={`text-2xl font-bold ${stats.netChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.netChange >= 0 ? '+' : ''}{stats.netChange}
                </p>
              </div>
              <Package className="h-8 w-8 text-blue-500" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <h3 className="font-medium text-gray-700">Filters</h3>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
          >
            {showFilters ? 'Hide' : 'Show'} Advanced Filters
          </button>
        </div>

        <div className="space-y-4">
          {/* Basic Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Date
              </label>
              <Input
                type="date"
                value={filters.from_date}
                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                icon={Calendar}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To Date
              </label>
              <Input
                type="date"
                value={filters.to_date}
                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                icon={Calendar}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Movement Type
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Types</option>
                {Object.values(MOVEMENT_TYPES).map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <Input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                icon={Search}
                placeholder="Search by item, reference..."
              />
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference Number
                </label>
                <Input
                  type="text"
                  value={filters.reference}
                  onChange={(e) => handleFilterChange('reference', e.target.value)}
                  placeholder="PO, invoice, etc."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Performed By
                </label>
                <Input
                  type="text"
                  value={filters.performed_by}
                  onChange={(e) => handleFilterChange('performed_by', e.target.value)}
                  placeholder="User name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <Input
                  type="text"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  placeholder="Storage location"
                />
              </div>
            </div>
          )}

          {/* Filter Actions */}
          <div className="flex justify-end space-x-3 pt-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleResetFilters}
            >
              Reset Filters
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (id) {
                  getItemMovements(id, filters);
                } else {
                  fetchMovements(filters);
                }
              }}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Movements Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                {!id && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performed By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reason
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={id ? 7 : 8} className="px-6 py-8">
                    <div className="flex justify-center">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : movements.length === 0 ? (
                <tr>
                  <td colSpan={id ? 7 : 8} className="px-6 py-8">
                    <div className="text-center">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No movements found</h3>
                      <p className="text-gray-500">
                        {id 
                          ? 'No stock movements recorded for this item yet.'
                          : 'No stock movements found matching your criteria.'}
                      </p>
                      {id && (
                        <Button
                          variant="primary"
                          onClick={() => setShowForm(true)}
                          className="mt-4"
                        >
                          Record First Movement
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                movements.map((movement) => (
                  <tr key={movement.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {format(new Date(movement.created_at), 'MMM dd, yyyy')}
                      </div>
                      <div className="text-sm text-gray-500">
                        {format(new Date(movement.created_at), 'HH:mm')}
                      </div>
                    </td>
                    {!id && (
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {movement.item_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {movement.sku}
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getMovementIcon(movement.type)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMovementTypeColor(movement.type)}`}>
                          {MOVEMENT_TYPES[movement.type?.toUpperCase()]?.name || movement.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`text-sm font-medium ${
                        ['in', 'return'].includes(movement.type) 
                          ? 'text-green-600' 
                          : ['out', 'damaged', 'expired'].includes(movement.type)
                          ? 'text-red-600'
                          : 'text-yellow-600'
                      }`}>
                        {['in', 'return'].includes(movement.type) ? '+' : '-'}
                        {movement.quantity} {movement.unit}
                      </span>
                      {movement.batch_number && (
                        <div className="text-xs text-gray-500">
                          Batch: {movement.batch_number}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{movement.reference || 'N/A'}</div>
                      {movement.supplier && (
                        <div className="text-xs text-gray-500">
                          Supplier: {movement.supplier}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{movement.performed_by || 'System'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {movement.reason}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleViewMovement(movement)}
                        className="p-1 text-gray-500 hover:text-primary-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <div className="text-sm text-gray-700">
              Showing page {pagination.currentPage} of {pagination.totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  if (id) {
                    getItemMovements(id, { ...filters, page: pagination.currentPage - 1 });
                  } else {
                    fetchMovements({ ...filters, page: pagination.currentPage - 1 });
                  }
                }}
                disabled={pagination.currentPage === 1}
                className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => {
                  if (id) {
                    getItemMovements(id, { ...filters, page: pagination.currentPage + 1 });
                  } else {
                    fetchMovements({ ...filters, page: pagination.currentPage + 1 });
                  }
                }}
                disabled={pagination.currentPage === pagination.totalPages}
                className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Movement Form Modal */}
      {showForm && item && (
        <StockMovementForm
          isOpen={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={async (data) => {
            await stockApi.createStockMovement(data);
            getItemMovements(id, filters);
            setShowForm(false);
          }}
          item={item}
        />
      )}

      {/* Movement Details Modal */}
      {selectedMovement && (
        <StockMovementDetails
          isOpen={!!selectedMovement}
          onClose={handleCloseDetails}
          movement={selectedMovement}
        />
      )}
    </div>
  );
};

export default StockMovementPage;